<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';

const props = defineProps({
  basketEndpoint: { type: String, required: true },
  deliveryEndpoint: { type: String, required: true },
  userEndpoint: { type: String, required: true },
  updateEndpoint: { type: String, required: true },
  paymentEndpoint: { type: String, required: true },
  citiesEndpoint: { type: String, required: true },
  warehousesEndpoint: { type: String, required: true },
  sentUrl: { type: String, required: true },
  registrationUrl: { type: String, required: true },
  offerUrl: { type: String, required: true }
});

const defaultUser = () => ({
  shop_surname: '',
  shop_name: '',
  shop_phone: '',
  shop_mail: '',
  shop_city: '',
  shop_city_fk: '',
  shop_city_href: '',
  shop_city_id: '',
  shop_warehouse: '',
  shop_warehouse_href: '',
  shop_adress: '',
  zip_address: '',
  shop_payment: '1',
  shop_dsc: '',
  delivery_id: ''
});

const localStorageKey = 'order';
const basket = ref([]);
const delivery = ref({});
const user = reactive(defaultUser());
const isRegistered = ref(false);
const isLoading = ref(true);
const isSubmitting = ref(false);
const requestError = ref('');
const submitAttempted = ref(false);

const citySuggestions = ref([]);
const warehouseSuggestions = ref([]);
const loadingCities = ref(false);
const noCities = ref(false);
const loadingWarehouses = ref(false);
const noWarehouses = ref(false);
const noLoadedCities = ref(false);

let citySearchTimer = null;
let warehouseSearchTimer = null;
let cityRequestId = 0;
let warehouseRequestId = 0;

const deliveryOptions = computed(() => Object.values(delivery.value || {}));

const selectedDelivery = computed(() => {
  return delivery.value?.[String(user.delivery_id)] || null;
});

const isFormValid = computed(() => {
  return (
    hasValue(user.shop_surname) &&
    hasValue(user.shop_name) &&
    hasValue(user.shop_phone) &&
    hasValue(user.shop_mail) &&
    hasValue(user.shop_city) &&
    hasValue(user.delivery_id) &&
    isValidEmail(user.shop_mail)
  );
});

const totalPrice = computed(() => {
  const basketTotal = basket.value.reduce((sum, product) => {
    return sum + Number(product.price || 0) * Number(product.pcs || 0);
  }, 0);

  if (String(user.delivery_id) === '1') {
    const deliveryPrice = Number(selectedDelivery.value?.delivery_price || 0);
    return (basketTotal + deliveryPrice).toFixed(2);
  }

  return basketTotal.toFixed(2);
});

onMounted(async () => {
  await loadCheckoutState();
});

watch(
  user,
  (nextUser) => {
    window.localStorage.setItem(localStorageKey, JSON.stringify(nextUser));
  },
  { deep: true }
);

async function loadCheckoutState() {
  isLoading.value = true;
  requestError.value = '';

  try {
    const [basketResponse, deliveryResponse, userResponse] = await Promise.all([
      fetchJson(props.basketEndpoint),
      fetchJson(props.deliveryEndpoint),
      fetchJson(props.userEndpoint)
    ]);

    basket.value = normalizeBasket(basketResponse?.success ? basketResponse.data : []);
    delivery.value = deliveryResponse?.success ? normalizeDelivery(deliveryResponse.data) : {};

    const storedUser = getStoredUser();
    const loadedUser = userResponse?.success ? userResponse.data : storedUser;
    isRegistered.value = Boolean(userResponse?.success);
    applyUser(loadedUser);

    if (!hasValue(user.shop_payment)) {
      user.shop_payment = '1';
    }

    updateDeliveryAvailability();
    sendBeginCheckout(basket.value);
  } catch (error) {
    requestError.value = 'Не удалось загрузить данные заказа. Попробуйте обновить страницу.';
  } finally {
    isLoading.value = false;
  }
}

function normalizeBasket(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === 'object') {
    return Object.values(data);
  }

  return [];
}

function normalizeDelivery(data) {
  return data && typeof data === 'object' ? data : {};
}

function getStoredUser() {
  try {
    const raw = window.localStorage.getItem(localStorageKey);
    if (!raw) {
      return defaultUser();
    }

    const parsed = JSON.parse(raw);
    return {
      ...defaultUser(),
      ...parsed,
      shop_payment: String(parsed.shop_payment || parsed.pay_type || '1'),
      delivery_id: parsed.delivery_id ? String(parsed.delivery_id) : ''
    };
  } catch (error) {
    return defaultUser();
  }
}

function applyUser(nextUser) {
  const normalized = {
    ...defaultUser(),
    ...(nextUser || {})
  };

  normalized.shop_payment = String(normalized.shop_payment || normalized.pay_type || '1');
  normalized.delivery_id = normalized.delivery_id ? String(normalized.delivery_id) : '';

  Object.assign(user, normalized);
}

function hasValue(value) {
  return String(value ?? '').trim() !== '';
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function sanitizeDigits(value) {
  return String(value ?? '').replace(/[^0-9]/g, '');
}

function fieldError(fieldName) {
  if (!submitAttempted.value) {
    return false;
  }

  switch (fieldName) {
    case 'surname':
      return !hasValue(user.shop_surname);
    case 'name':
      return !hasValue(user.shop_name);
    case 'phone':
      return !hasValue(user.shop_phone);
    case 'email':
      return !hasValue(user.shop_mail) || !isValidEmail(user.shop_mail);
    case 'city':
      return !hasValue(user.shop_city);
    case 'delivery':
      return !hasValue(user.delivery_id);
    default:
      return false;
  }
}

function sendBeginCheckout(products) {
  if (!window.stats || typeof window.stats.sendBeginCheckout !== 'function') {
    return;
  }

  const data = products.map((product) => ({
    name: product.good_name,
    key: product['1c_key'],
    price: product.price,
    currency: 'UAH',
    qnt: product.pcs
  }));

  window.stats.sendBeginCheckout(data);
}

function updateDeliveryAvailability() {
  const pickup = delivery.value?.['1'];
  if (!pickup) {
    return;
  }

  pickup.disabled = String(user.shop_city_id) !== '10';
}

function onPhoneInput() {
  user.shop_phone = sanitizeDigits(user.shop_phone);
}

function onCityInput() {
  user.shop_city_href = '';
  user.shop_city_fk = '';
  user.shop_city_id = '';
  user.shop_warehouse = '';
  user.shop_warehouse_href = '';
  warehouseSuggestions.value = [];
  noLoadedCities.value = false;
  updateDeliveryAvailability();
  queueCitySearch();
}

function onWarehouseInput() {
  user.shop_warehouse_href = '';
  queueWarehouseSearch();
}

function queueCitySearch() {
  window.clearTimeout(citySearchTimer);

  const query = String(user.shop_city || '').trim();
  if (query.length < 2) {
    citySuggestions.value = [];
    noCities.value = false;
    loadingCities.value = false;
    return;
  }

  citySearchTimer = window.setTimeout(() => {
    fetchCities(query);
  }, 300);
}

function queueWarehouseSearch() {
  window.clearTimeout(warehouseSearchTimer);

  const query = String(user.shop_warehouse || '').trim();
  if (!hasValue(user.shop_city_href)) {
    noLoadedCities.value = true;
    warehouseSuggestions.value = [];
    return;
  }

  noLoadedCities.value = false;

  if (query.length < 1) {
    warehouseSuggestions.value = [];
    noWarehouses.value = false;
    loadingWarehouses.value = false;
    return;
  }

  warehouseSearchTimer = window.setTimeout(() => {
    fetchWarehouses(query);
  }, 300);
}

async function fetchCities(query) {
  const currentRequestId = ++cityRequestId;
  loadingCities.value = true;
  noCities.value = false;

  try {
    const response = await fetchJson(
      `${props.citiesEndpoint}&search_word=${encodeURIComponent(query)}`
    );

    if (currentRequestId !== cityRequestId) {
      return;
    }

    citySuggestions.value = response?.success ? response.data || [] : [];
    noCities.value = citySuggestions.value.length === 0;
  } catch (error) {
    if (currentRequestId !== cityRequestId) {
      return;
    }

    citySuggestions.value = [];
    noCities.value = true;
  } finally {
    if (currentRequestId === cityRequestId) {
      loadingCities.value = false;
    }
  }
}

async function fetchWarehouses(query) {
  const currentRequestId = ++warehouseRequestId;
  loadingWarehouses.value = true;
  noWarehouses.value = false;

  try {
    const response = await fetchJson(
      `${props.warehousesEndpoint}&city_href=${encodeURIComponent(user.shop_city_href)}&search_word=${encodeURIComponent(query)}`
    );

    if (currentRequestId !== warehouseRequestId) {
      return;
    }

    warehouseSuggestions.value = response?.success ? response.data || [] : [];
    noWarehouses.value = warehouseSuggestions.value.length === 0;
  } catch (error) {
    if (currentRequestId !== warehouseRequestId) {
      return;
    }

    warehouseSuggestions.value = [];
    noWarehouses.value = true;
  } finally {
    if (currentRequestId === warehouseRequestId) {
      loadingWarehouses.value = false;
    }
  }
}

function selectCity(item) {
  user.shop_city_fk = item.id;
  user.shop_city = item.city_name;
  user.shop_city_href = item.city_ref;
  user.shop_city_id = item.city_id;
  user.shop_warehouse = '';
  user.shop_warehouse_href = '';
  citySuggestions.value = [];
  noCities.value = false;
  updateDeliveryAvailability();
}

function selectWarehouse(item) {
  user.shop_warehouse_href = item.warehouse_ref;
  user.shop_warehouse = item.warehouse_name;
  warehouseSuggestions.value = [];
  noWarehouses.value = false;
}

function hideCitySuggestions() {
  window.setTimeout(() => {
    citySuggestions.value = [];
  }, 150);
}

function hideWarehouseSuggestions() {
  window.setTimeout(() => {
    warehouseSuggestions.value = [];
  }, 150);
}

function deliveryPriceText(item) {
  if (!item) {
    return '';
  }

  if (String(user.delivery_id) === '1' || String(user.delivery_id) === '2') {
    return item.delivery_price;
  }

  return 'Согласно тарифам перевозчика';
}

function deliverySumText(item) {
  if (!item) {
    return '';
  }

  if (String(user.delivery_id) === '1' || String(user.delivery_id) === '2') {
    return `${item.delivery_price} грн`;
  }

  return '';
}

async function storeParams() {
  return fetchJson(props.updateEndpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({ ...user })
  });
}

async function submitOrder() {
  submitAttempted.value = true;
  requestError.value = '';

  if (!isFormValid.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;

  try {
    const response = await storeParams();
    if (!response?.success) {
      throw new Error('update failed');
    }

    window.location.assign(props.sentUrl);
  } catch (error) {
    requestError.value = 'Не удалось сохранить заказ. Попробуйте еще раз.';
  } finally {
    isSubmitting.value = false;
  }
}

async function payOrder() {
  submitAttempted.value = true;
  requestError.value = '';

  if (!isFormValid.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;

  try {
    const updateResponse = await storeParams();
    if (!updateResponse?.success) {
      throw new Error('update failed');
    }

    const paymentResponse = await fetchJson(props.paymentEndpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ ...user })
    });

    if (!paymentResponse?.success || !paymentResponse.data?.data || !paymentResponse.data?.signature) {
      throw new Error('payment failed');
    }

    window.location.assign(
      `https://www.liqpay.ua/api/3/checkout?data=${paymentResponse.data.data}&signature=${paymentResponse.data.signature}`
    );
  } catch (error) {
    requestError.value = 'Не удалось подготовить оплату. Попробуйте еще раз.';
  } finally {
    isSubmitting.value = false;
  }
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    },
    ...options
  });

  return response.json();
}
</script>

<template>
  <div class="vue-checkout">
    <div v-if="isLoading" class="vue-checkout__status">
      Загрузка...
    </div>

    <div v-else class="order-content-box-text">
      <div v-if="requestError" class="alert alert-danger" role="alert">
        {{ requestError }}
      </div>

      <div class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div v-if="!isRegistered" class="alert alert-info" role="alert">
            <p>Если вы покупаете у нас не в первый раз.</p>
            <strong><a :href="registrationUrl">Регистрация</a></strong> сократит время оформления заказа.
          </div>
        </div>
      </div>

      <div class="row">
        <p class="titl">Укажите контакты</p>
        <div class="col-lg-5 col-sm-5 col-xs-10">
          <div class="input-group input-group-sm">
            <span class="input-group-addon" id="order_surname">Фамилия<font color="red">*</font>:</span>
            <input
              v-model.trim="user.shop_surname"
              type="text"
              class="form-control"
              aria-describedby="order_surname"
              name="surname"
            >
          </div>
          <span v-if="fieldError('surname')" style="color:red">
            <span>Обязательное поле.</span>
          </span>
        </div>
        <div class="col-lg-5 col-sm-5 col-xs-10">
          <div class="input-group input-group-sm">
            <span class="input-group-addon" id="order_name">Имя <font color="red">*</font>:</span>
            <input
              v-model.trim="user.shop_name"
              type="text"
              class="form-control"
              aria-describedby="order_name"
              name="name"
            >
          </div>
          <span v-if="fieldError('name')" style="color:red">
            <span>Обязательное поле.</span>
          </span>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-5 col-sm-5 col-xs-10">
          <div class="input-group input-group-sm">
            <span class="input-group-addon" id="order_phone">Телефон <font color="red">*</font>:</span>
            <input
              v-model="user.shop_phone"
              type="text"
              class="form-control"
              name="phone"
              placeholder="0ХХХХХХХХХ"
              @input="onPhoneInput"
            >
          </div>
          <span v-if="fieldError('phone')" style="color:red">
            <span>Обязательное поле.</span>
          </span>
        </div>

        <div class="col-lg-5 col-sm-5 col-xs-10">
          <div class="input-group input-group-sm">
            <span class="input-group-addon" id="order_email">E-mail <font color="red">*</font>:</span>
            <input
              v-model.trim="user.shop_mail"
              type="email"
              class="form-control"
              aria-describedby="order_email"
              name="email"
            >
          </div>
          <span v-if="fieldError('email')" style="color:red">
            <span v-if="!hasValue(user.shop_mail)">Обязательное поле.</span>
            <span v-else>Неверный формат email.</span>
          </span>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-5 col-sm-5 col-xs-10">
          <div class="input-group input-group-sm vue-checkout__autocomplete">
            <span class="input-group-addon" id="order_city">Город <font color="red">*</font>:</span>
            <input
              v-model.trim="user.shop_city"
              type="text"
              class="form-control"
              aria-describedby="order_city"
              placeholder="Выберите город"
              autocomplete="off"
              @input="onCityInput"
              @blur="hideCitySuggestions"
            >
            <ul v-if="citySuggestions.length" class="vue-checkout__suggestions">
              <li
                v-for="item in citySuggestions"
                :key="item.city_ref"
                @mousedown.prevent="selectCity(item)"
              >
                {{ item.city_name }}
              </li>
            </ul>
          </div>
          <div v-if="loadingCities">Поиск...</div>
          <div v-if="noCities">Не найдено</div>
          <span v-if="fieldError('city')" style="color:red">Обязательное поле.</span>
        </div>

        <div class="col-lg-5 col-sm-5 col-xs-10">
          <div class="input">
            <select v-model="user.delivery_id" class="form-control select-delivery" name="type">
              <option value="">--- Выберите тип доставки ---</option>
              <option
                v-for="type in deliveryOptions"
                :key="type.id"
                :value="String(type.id)"
                :disabled="Boolean(type.disabled)"
              >
                {{ type.delivery_name }}
              </option>
            </select>
          </div>
          <span v-if="fieldError('delivery')" style="color:red">Обязательное поле.</span>
        </div>
      </div>

      <div v-if="user.delivery_id === '4'" class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="input-group input-group-sm vue-checkout__autocomplete">
            <span class="input-group-addon" id="order_warehouse">Отделение <font color="red">*</font>:</span>
            <input
              v-model.trim="user.shop_warehouse"
              type="text"
              class="form-control"
              aria-describedby="order_warehouse"
              placeholder="Выберите отделение"
              autocomplete="off"
              @input="onWarehouseInput"
              @blur="hideWarehouseSuggestions"
            >
            <ul v-if="warehouseSuggestions.length" class="vue-checkout__suggestions">
              <li
                v-for="item in warehouseSuggestions"
                :key="item.warehouse_ref"
                @mousedown.prevent="selectWarehouse(item)"
              >
                {{ item.warehouse_name }}
              </li>
            </ul>
          </div>
          <div v-if="loadingWarehouses">Поиск...</div>
          <div v-if="noWarehouses">Не найдено</div>
          <div v-if="noLoadedCities">
            <font color="red">Сначала выберите город.</font>
          </div>
        </div>
      </div>

      <div v-if="user.delivery_id === '1' || user.delivery_id === '3'" class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="input-group input-group-sm">
            <span class="input-group-addon" id="order_delivery">Адрес доставки</span>
            <input
              v-model.trim="user.shop_adress"
              type="text"
              class="form-control"
              aria-describedby="order_delivery"
              name="adress"
            >
          </div>
        </div>
      </div>

      <div v-if="user.delivery_id === '5'" class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="input-group input-group-sm">
            <span class="input-group-addon" id="zip_address">Почтовый индекс</span>
            <input
              v-model.trim="user.zip_address"
              type="text"
              class="form-control"
              aria-describedby="zip_address"
              name="zip_adress"
            >
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="form-group">
            <label for="order_coment">Выберите тип оплаты:</label>
            <label class="radio-inline">
              <input v-model="user.shop_payment" type="radio" value="1" name="order_paytype">
              Оплата наличными
            </label>
            <label class="radio-inline">
              <input v-model="user.shop_payment" type="radio" value="2" name="order_paytype">
              Наложенный платеж
            </label>
            <label class="radio-inline">
              <input v-model="user.shop_payment" type="radio" value="3" name="order_paytype">
              Оплата картой Visa/Mastercard
            </label>
          </div>
        </div>
        <div v-if="user.shop_payment === '33'" class="col-lg-11 col-sm-11 col-xs-10">
          <div class="alert alert-warning" role="alert">
            <p>После обработки заказа на ваш email будет отправлена персональная ссылка для оплаты.</p>
          </div>
        </div>
      </div>

      <div v-if="user.delivery_id === '2'" class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="alert alert-warning" role="alert">
            <span style="color:red;">Растения с открытой корневой системой отправляются только по согласованию.<br><strong>Вы соглашаетесь с условиями доставки.</strong></span>
          </div>
        </div>
      </div>

      <div v-if="user.delivery_id === '3'" class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="alert alert-success" role="alert">
            <ul>
              <li>Если вы заказываете растения по индивидуальному заказу, требуется <span style="color:red;">100% предоплата</span>.</li>
              <li>Если вы заказываете товары из наличия, требуется <span style="color:red;">предоплата 20%</span>, но не менее установленного минимума.</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <div class="form-group">
            <label for="order_coment">Комментарий к доставке:</label>
            <textarea
              id="order_coment"
              v-model.trim="user.shop_dsc"
              name="dsc"
              class="form-control"
              rows="3"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-11 col-sm-11 col-xs-10">
          <p class="titl">Детали заказа:</p>
          <table class="table">
            <tr bgcolor="#efefef" class="basket_missed">
              <td><strong>Название товара</strong></td>
              <td><strong>Кол-во</strong></td>
              <td><strong>Цена</strong></td>
              <td><strong>Сумма</strong></td>
            </tr>
            <tr v-for="(product, index) in basket" :key="product.good_id || index" class="basket_row">
              <td class="product_name">
                {{ product.good_name }}<br>
                <span style="font-size:9px;" class="product_id">{{ product.dsc }}</span>
              </td>
              <td class="product_qty">{{ product.pcs }}</td>
              <td class="price">{{ product.price }}</td>
              <td>{{ (Number(product.price || 0) * Number(product.pcs || 0)).toFixed(2) }}&nbsp;грн</td>
            </tr>
            <tr v-if="selectedDelivery" class="basket_row">
              <td>
                Доставка: <span style="font-size:10px;">{{ selectedDelivery.delivery_name }}</span>
              </td>
              <td>1</td>
              <td>{{ deliveryPriceText(selectedDelivery) }}</td>
              <td>{{ deliverySumText(selectedDelivery) }}</td>
            </tr>
            <tr class="basket_missed">
              <td colspan="4" align="right">Итого: <span id="total_price">{{ totalPrice }}</span> грн</td>
            </tr>
          </table>

          <div class="form-group">
            <div class="red"><font color="red">*</font> Поля, отмеченные звездочкой, обязательны для заполнения</div>
            <div style="text-align:center">
              Я ознакомился с <a :href="offerUrl">условиями использования</a>.
            </div>
          </div>

          <div v-if="user.shop_payment === '3'" style="text-align:center">
            <a href="#" @click.prevent="payOrder">
              <span class="btn" :class="!isFormValid || isSubmitting ? 'btn-default' : 'btn-success'">
                <sub></sub><em>Оплатить заказ</em><sup></sup>
              </span>
            </a>
          </div>
          <div style="text-align:center">
            <a
              v-if="user.shop_payment !== '3'"
              href="#"
              @click.prevent="submitOrder"
            >
              <span class="btn" :class="!isFormValid || isSubmitting ? 'btn-default' : 'btn-success'">
                <sub></sub><em>Подтвердить заказ</em><sup></sup>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
